<template>
  <div class="annotation-list-panel">
    <div class="panel-header">
      <h4 class="panel-title">
        <i class="fas fa-list"></i>
        {{ $t('annotation.annotationList') }}
      </h4>
      <span class="annotation-count">{{ filteredAnnotations.length }}</span>
    </div>

    <div class="panel-controls">
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="$t('annotation.searchAnnotations')"
        class="search-input"
      >
      <select v-model="filterType" class="filter-select">
        <option value="all">{{ $t('annotation.allTypes') }}</option>
        <option value="text">{{ $t('annotation.text') }}</option>
        <option value="highlight">{{ $t('annotation.highlight') }}</option>
        <option value="rectangle">{{ $t('annotation.rectangle') }}</option>
        <option value="circle">{{ $t('annotation.circle') }}</option>
        <option value="freehand">{{ $t('annotation.freehand') }}</option>
        <option value="measure-distance">{{ $t('annotation.measureDistance') }}</option>
        <option value="measure-area">{{ $t('annotation.measureArea') }}</option>
      </select>
    </div>

    <div class="annotation-items">
      <div
        v-for="annotation in filteredAnnotations"
        :key="annotation.id"
        :class="['annotation-item', { selected: selectedAnnotationId === annotation.id }]"
        @click="$emit('annotation-select', annotation)"
      >
        <div class="annotation-icon" :style="{ color: annotation.color }">
          <i :class="getAnnotationIcon(annotation.type)"></i>
        </div>
        <div class="annotation-content">
          <div class="annotation-header">
            <span class="annotation-type">{{ $t(`annotation.${annotation.type}`) }}</span>
            <span v-if="annotation.pageNumber" class="page-badge">
              P{{ annotation.pageNumber }}
            </span>
          </div>
          <div v-if="annotation.text" class="annotation-text">
            {{ truncateText(annotation.text, 50) }}
          </div>
          <div class="annotation-meta">
            <span class="annotation-time">{{ formatTime(annotation.timestamp) }}</span>
            <span v-if="annotation.author" class="annotation-author">{{ annotation.author }}</span>
          </div>
        </div>
        <div class="annotation-actions">
          <button
            class="action-btn"
            :title="$t('annotation.jumpTo')"
            @click.stop="$emit('annotation-jump', annotation)"
          >
            <i class="fas fa-crosshairs"></i>
          </button>
          <button
            class="action-btn action-btn--danger"
            :title="$t('common.delete')"
            @click.stop="$emit('annotation-delete', annotation)"
          >
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>

      <div v-if="filteredAnnotations.length === 0" class="empty-state">
        <i class="fas fa-inbox"></i>
        <p>{{ searchQuery ? $t('annotation.noMatchingAnnotations') : $t('annotation.noAnnotations') }}</p>
      </div>
    </div>

    <div class="panel-footer">
      <button class="footer-btn" @click="$emit('export-list')">
        <i class="fas fa-download"></i>
        {{ $t('annotation.exportList') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { AnnotationListItem, AnnotationTool } from '@/types/annotation';

interface Props {
  annotations: AnnotationListItem[];
  selectedAnnotationId?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'annotation-select', annotation: AnnotationListItem): void;
  (e: 'annotation-jump', annotation: AnnotationListItem): void;
  (e: 'annotation-delete', annotation: AnnotationListItem): void;
  (e: 'export-list'): void;
}>();

const { t } = useI18n();

const searchQuery = ref('');
const filterType = ref('all');

const filteredAnnotations = computed(() => {
  let filtered = props.annotations;

  // Filter by type
  if (filterType.value !== 'all') {
    filtered = filtered.filter((a: AnnotationListItem) => a.type === filterType.value);
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter((a: AnnotationListItem) => 
      (a.text && a.text.toLowerCase().includes(query)) ||
      a.type.toLowerCase().includes(query) ||
      (a.author && a.author.toLowerCase().includes(query))
    );
  }

  return filtered;
});

const getAnnotationIcon = (type: AnnotationTool): string => {
  const iconMap: Record<string, string> = {
    'text': 'fas fa-font',
    'highlight': 'fas fa-highlighter',
    'rectangle': 'fas fa-square',
    'circle': 'fas fa-circle',
    'freehand': 'fas fa-pen',
    'arrow': 'fas fa-arrow-right',
    'line': 'fas fa-minus',
    'measure-distance': 'fas fa-ruler',
    'measure-area': 'fas fa-draw-polygon',
  };
  return iconMap[type] || 'fas fa-pencil-alt';
};

const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const formatTime = (timestamp: Date): string => {
  const now = new Date();
  const diff = now.getTime() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return t('annotation.justNow');
  if (minutes < 60) return t('annotation.minutesAgo', { count: minutes });
  if (hours < 24) return t('annotation.hoursAgo', { count: hours });
  if (days < 7) return t('annotation.daysAgo', { count: days });
  
  return new Date(timestamp).toLocaleDateString();
};
</script>

<style scoped>
.annotation-list-panel {
  width: 320px;
  height: 100%;
  background: var(--surface-secondary);
  border-left: 1px solid var(--border-light);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: var(--space-md);
  border-bottom: 1px solid var(--border-light);
  background: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  display: flex;
  align-items: center;
  gap: var(--space-xs);
}

.annotation-count {
  background: var(--primary);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
}

.panel-controls {
  padding: var(--space-md);
  background: white;
  border-bottom: 1px solid var(--border-light);
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.search-input {
  width: 100%;
  padding: var(--space-sm);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary);
}

.filter-select {
  width: 100%;
  padding: var(--space-sm);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  background: white;
  cursor: pointer;
}

.filter-select:focus {
  outline: none;
  border-color: var(--primary);
}

.annotation-items {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-sm);
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.annotation-item {
  background: white;
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius);
  padding: var(--space-sm);
  display: flex;
  gap: var(--space-sm);
  cursor: pointer;
  transition: all 0.2s;
}

.annotation-item:hover {
  border-color: var(--primary);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.annotation-item.selected {
  border-color: var(--primary);
  background: var(--primary-light);
}

.annotation-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-secondary);
  border-radius: var(--border-radius);
  font-size: var(--font-size-lg);
}

.annotation-content {
  flex: 1;
  min-width: 0;
}

.annotation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-xs);
  gap: var(--space-xs);
}

.annotation-type {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.page-badge {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  background: var(--surface-secondary);
  padding: 2px 6px;
  border-radius: 4px;
}

.annotation-text {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin-bottom: var(--space-xs);
  word-break: break-word;
}

.annotation-meta {
  display: flex;
  gap: var(--space-xs);
  font-size: var(--font-size-xs);
  color: var(--text-tertiary);
}

.annotation-time {
  display: flex;
  align-items: center;
  gap: 4px;
}

.annotation-author::before {
  content: '•';
  margin-right: 4px;
}

.annotation-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-secondary);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
  font-size: var(--font-size-sm);
}

.action-btn:hover {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

.action-btn--danger:hover {
  background: var(--danger);
  border-color: var(--danger);
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
  text-align: center;
  padding: var(--space-xl);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: var(--space-md);
  opacity: 0.5;
}

.panel-footer {
  padding: var(--space-md);
  border-top: 1px solid var(--border-light);
  background: white;
}

.footer-btn {
  width: 100%;
  padding: var(--space-sm) var(--space-md);
  background: var(--surface-secondary);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-xs);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.footer-btn:hover {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

/* Scrollbar styling */
.annotation-items::-webkit-scrollbar {
  width: 8px;
}

.annotation-items::-webkit-scrollbar-track {
  background: var(--surface-secondary);
}

.annotation-items::-webkit-scrollbar-thumb {
  background: var(--border-medium);
  border-radius: 4px;
}

.annotation-items::-webkit-scrollbar-thumb:hover {
  background: var(--text-secondary);
}
</style>
